import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Calendar, User, Mail, Phone as PhoneIcon, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import WhatsAppFallback from '../components/WhatsAppFallback';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Booking = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    projectType: '',
    budget: '',
    preferredDate: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const services = [
    'Swimming Pool Design & Construction',
    'Pool Maintenance & AMC',
    'Pool Renovation & Repairs',
    'Fitness Equipment & Solutions'
  ];

  const projectTypes = [
    'Residential - New Construction',
    'Residential - Renovation',
    'Commercial - Hotel/Resort',
    'Commercial - Gym/Fitness Center',
    'Apartment Complex',
    'Other'
  ];

  const budgetRanges = [
    'Under ₹5 Lakhs',
    '₹5-10 Lakhs',
    '₹10-20 Lakhs',
    '₹20-50 Lakhs',
    'Above ₹50 Lakhs'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/booking`, formData);
      toast.success('Quote request submitted successfully! We will contact you within 24 hours.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        projectType: '',
        budget: '',
        preferredDate: '',
        message: ''
      });
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      toast.error('Failed to submit request. Please call us directly at 077579 99805.');
      console.error('Booking form error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1695592690356-1266107c9471?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBwb29sJTIwZGVzaWdufGVufDB8fHx8MTc3MDA1MjE3NXww&ixlib=rb-4.1.0&q=85"
            alt="Request a Quote"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-navy-900/70"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4">
          <img 
            src="/assets/logo.png" 
            alt="Aquarist Pools Logo"
            width="80"
            height="80"
            loading="lazy"
            decoding="async"
            className="w-20 h-20 mx-auto mb-4 bg-white/10 backdrop-blur-sm rounded-full p-2"
          />
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Request a Quote</h1>
          <p className="text-xl md:text-2xl mb-2">Let's Build Your Dream Pool Together</p>
          <p className="text-lg text-aqua-300 font-semibold">Complete Pool & Spa Solutions</p>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-20 bg-gradient-to-b from-white to-cream-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-aqua-500 to-aqua-600 text-white p-8">
              <CardTitle className="text-3xl">Tell Us About Your Project</CardTitle>
              <p className="text-aqua-50 mt-2">Fill out the form below and we'll get back to you within 24 hours</p>
            </CardHeader>
            
            <CardContent className="p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 mr-2 text-aqua-600" />
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4 mr-2 text-aqua-600" />
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <PhoneIcon className="w-4 h-4 mr-2 text-aqua-600" />
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 XXXXX XXXXX"
                    className="h-12"
                  />
                </div>

                {/* Service Selection */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service Required *
                    </label>
                    <Select onValueChange={(value) => handleSelectChange('service', value)} required>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service} value={service}>
                            {service}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Type *
                    </label>
                    <Select onValueChange={(value) => handleSelectChange('projectType', value)} required>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select project type" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estimated Budget
                    </label>
                    <Select onValueChange={(value) => handleSelectChange('budget', value)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Select budget range" />
                      </SelectTrigger>
                      <SelectContent>
                        {budgetRanges.map((range) => (
                          <SelectItem key={range} value={range}>
                            {range}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4 mr-2 text-aqua-600" />
                      Preferred Start Date
                    </label>
                    <Input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      className="h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <MessageSquare className="w-4 h-4 mr-2 text-aqua-600" />
                    Project Details *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us more about your project... (pool size, special features, location, etc.)"
                    rows={6}
                    className="resize-none"
                  />
                </div>

                <div className="bg-aqua-50 border border-aqua-200 rounded-lg p-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-aqua-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-navy-900 mb-2">What Happens Next?</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• We'll review your request within 24 hours</li>
                        <li>• Our expert will call you to discuss details</li>
                        <li>• Schedule a site visit at your convenience</li>
                        <li>• Receive a detailed quote and timeline</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  size="lg"
                  className="w-full bg-gradient-to-r from-aqua-500 to-aqua-600 hover:from-aqua-600 hover:to-aqua-700 text-white py-6 text-lg transition-all duration-300 hover:scale-105"
                >
                  {loading ? 'Submitting...' : 'Submit Quote Request'}
                </Button>
                <WhatsAppFallback
                  prefill={`Hi Aquarist Pools, I'd like a quote for ${formData.service || '[service]'} — Project Type: ${formData.projectType || '[type]'}. My name is ${formData.name || '[Name]'}, phone: ${formData.phone || '[phone]'}.`}
                  label="Or send this quote request via WhatsApp"
                />
              </form>
            </CardContent>
          </Card>

          {/* Alternative Contact */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Prefer to talk directly?</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg"
                onClick={() => window.location.href = 'tel:+917757999805'}
                className="bg-aqua-500 hover:bg-aqua-600 text-white px-8"
              >
                <PhoneIcon className="mr-2 h-5 w-5" />
                Call 077579 99805
              </Button>
              <Button 
                size="lg"
                onClick={() => window.open('https://wa.me/917757999805?text=Hello%2C%20I%20would%20like%20to%20get%20a%20quote', '_blank')}
                className="bg-green-500 hover:bg-green-600 text-white px-8"
              >
                WhatsApp Us
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
